import React from "react";
import { Link } from "react-router-dom";
import { heroContent } from "../mock";
import { useModeContext } from "../contexts/ModeContext";

const Footer = () => {
  const c = heroContent.colors;
  const { activeMode, setActiveMode } = useModeContext();

  return (
    <footer className="relative bg-neutral-900 text-white dark:bg-neutral-950 dark:text-neutral-100">
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <div className="mb-3">
                <div className="text-[13px] tracking-[0.12em] uppercase text-neutral-400 dark:text-neutral-500 font-medium">Tom</div>
                <div className="text-[15px] tracking-[0.12em] uppercase font-semibold" style={{ color: "#F5F5F5" }}>Oppenhäuser</div>
              </div>
              <p className="text-lg font-medium text-neutral-300 mb-4">
                {activeMode === 0 ? "Behaviour Change Coach" : "Executive & Peak Performance Coach"}
              </p>
              <p className="text-sm text-neutral-400 leading-relaxed max-w-md">
                {activeMode === 0 
                  ? "Nachhaltige Verhaltensveränderung durch fundierte psychologische Erkenntnisse und praktische Tools. Für Führungskräfte, Teams und alle, die echte Veränderung wollen."
                  : "Strategische Begleitung für Führungskräfte und High Performer. Mentale, emotionale und strategische Optimierung für nachhaltige Höchstleistung in Beruf und Leben."
                }
              </p>
            </div>

            {/* Segmented Control - Footer Version */}
            <div className="mb-6">
              <div className="flex items-center rounded-full border border-neutral-700 bg-neutral-800/60 backdrop-blur-md px-1 py-1 text-xs text-neutral-300 select-none w-fit">
                <button 
                  onClick={() => setActiveMode(0)}
                  className={`relative px-3 py-1.5 rounded-full transition-all duration-300 ${
                    activeMode === 0 
                      ? "bg-gradient-to-br from-[var(--brand-accent)] to-[color-mix(in_srgb,var(--brand-accent)_85%,#000_15%)] text-white font-medium shadow-lg" 
                      : "text-neutral-400 hover:text-neutral-200"
                  }`}
                >
                  Behaviour Change
                </button>
                <button 
                  onClick={() => setActiveMode(1)}
                  className={`relative px-3 py-1.5 rounded-full transition-all duration-300 ${
                    activeMode === 1 
                      ? "bg-gradient-to-br from-[var(--brand-accent)] to-[color-mix(in_srgb,var(--brand-accent)_85%,#000_15%)] text-white font-medium shadow-lg" 
                      : "text-neutral-400 hover:text-neutral-200"
                  }`}
                >
                  Peak Performance
                </button>
              </div>
            </div>
            
            {/* Social Links */}
            <div className="flex space-x-4">
              <a 
                href="https://www.instagram.com/tomoppenhauser?igsh=MXFtZ3pkYm5rMzE1bQ%3D%3D&utm_source=qr" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a 
                href="https://www.tiktok.com/@tom.oppenhaeuser?_t=ZN-8yyhumYA4uI&_r=1" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-neutral-800 hover:bg-neutral-700 flex items-center justify-center transition-colors"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-neutral-300 mb-4">
              Services
            </h4>
            <ul className="space-y-3">
              {activeMode === 0 ? (
                <>
                  <li>
                    <a href="#kompetenzen" className="text-sm text-neutral-400 hover:text-white transition-colors">
                      Einzelcoaching
                    </a>
                  </li>
                  <li>
                    <a href="#kompetenzen" className="text-sm text-neutral-400 hover:text-white transition-colors">
                      Gruppencoaching
                    </a>
                  </li>
                  <li>
                    <a href="#kompetenzen" className="text-sm text-neutral-400 hover:text-white transition-colors">
                      Keynote-Seminare
                    </a>
                  </li>
                  <li>
                    <a href="#coaching" className="text-sm text-neutral-400 hover:text-white transition-colors">
                      Behaviour Change
                    </a>
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <a href="#kompetenzen" className="text-sm text-neutral-400 hover:text-white transition-colors">
                      Executive Coaching
                    </a>
                  </li>
                  <li>
                    <a href="#kompetenzen" className="text-sm text-neutral-400 hover:text-white transition-colors">
                      Leadership Development
                    </a>
                  </li>
                  <li>
                    <a href="#kompetenzen" className="text-sm text-neutral-400 hover:text-white transition-colors">
                      Keynote-Speaking
                    </a>
                  </li>
                  <li>
                    <a href="#coaching" className="text-sm text-neutral-400 hover:text-white transition-colors">
                      Peak Performance
                    </a>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wide text-neutral-300 mb-4">
              Navigation
            </h4>
            <ul className="space-y-3">
              <li>
                <a href="#kompetenzen" className="text-sm text-neutral-400 hover:text-white transition-colors">
                  Kompetenzen
                </a>
              </li>
              <li>
                <a href="#coaching" className="text-sm text-neutral-400 hover:text-white transition-colors">
                  {activeMode === 0 ? "Was dich erwartet" : "Der Ansatz"}
                </a>
              </li>
              <li>
                <a href="#termin" className="text-sm text-neutral-400 hover:text-white transition-colors">
                  Termin vereinbaren
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-neutral-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-sm text-neutral-400">
              © Tom Oppenhäuser. Alle Rechte vorbehalten.
            </div>
            <div className="flex space-x-6">
              <Link to="/datenschutz" className="text-sm text-neutral-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-neutral-900 rounded">
                Datenschutz
              </Link>
              <Link to="/impressum" className="text-sm text-neutral-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-neutral-900 rounded">
                Impressum
              </Link>
              <Link to="/agb" className="text-sm text-neutral-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-neutral-900 rounded">
                AGB
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
    </footer>
  );
};

export default Footer;
